import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ChevronRight, Activity, PersonStanding, Sun, Star, Quote } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const testimonials = [
  { quote: "The decompression therapy saved me from back surgery. I am forever grateful to the team at Woods Spine & Wellness for giving me my life back. The $79 special was the best investment I ever made.", name: "Michael S.", service: "DRX9000 Decompression" },
  { quote: "The SoftWave therapy has been a game changer for my knee pain. I can finally walk up and down the stairs without wincing. Highly recommend taking advantage of their introductory offer!", name: "Sarah J.", service: "SoftWave TRT" },
  { quote: "I started the ChiroThin program with the 9-day trial and the results have been amazing. Not only did I lose weight, but I have so much more energy now.", name: "David L.", service: "ChiroThin Weight Loss" },
  { quote: "Red light therapy helped clear up my skin and reduced my joint inflammation. It's so relaxing, I look forward to my sessions every week. The intro special is a must-try.", name: "Emily R.", service: "Red Light Therapy" },
  { quote: "I was skeptical at first, but after my first SoftWave session, the sharp pain in my shoulder was significantly reduced. The staff is incredibly knowledgeable and caring.", name: "Robert M.", service: "SoftWave TRT" },
  { quote: "After years of dealing with sciatica, I finally found relief with the decompression special. Dr. Woods is amazing, and the whole clinic is top-notch.", name: "Jennifer T.", service: "DRX9000 Decompression" },
  { quote: "My foot pain was so severe I could barely walk. The SoftWave special got me in the door, and the results kept me coming back. I'm pain-free now!", name: "William B.", service: "SoftWave TRT" },
];
import { SEOHead } from "@/components/SEOHead";

export default function Specials() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="New Patient Specials | Woods Spine & Wellness | Longview TX"
        description="Take advantage of our exclusive new patient specials for ChiroThin, DRX9000 Decompression, Red Light Therapy, and SoftWave TRT in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        canonical="https://woodsspine.com/specials"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-6">
            New Patient <span className="text-accent">Special Offers</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Take the first step towards a pain-free life. Explore our exclusive introductory offers for our advanced therapies and discover how we can help you get back to a better you.
          </p>
        </section>

        {/* Testimonials Carousel */}
        <section className="container mx-auto px-4 mb-20 max-w-5xl">
          <div className="bg-primary/5 rounded-3xl p-8 md:p-12 border border-primary/10 relative">
            <Quote className="absolute top-8 left-8 w-16 h-16 text-primary/10 -z-0 rotate-180" />
            <Carousel
              opts={{
                align: "center",
                loop: true,
              }}
              className="w-full max-w-4xl mx-auto relative z-10"
            >
              <CarouselContent>
                {testimonials.map((testimonial, index) => (
                  <CarouselItem key={index}>
                    <div className="flex flex-col items-center text-center px-4 md:px-12">
                      <div className="flex gap-1 text-yellow-500 mb-6">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-current" />
                        ))}
                      </div>
                      <p className="text-xl md:text-2xl font-medium italic text-foreground/90 mb-8 leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center justify-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-foreground">{testimonial.name}</div>
                          <div className="text-sm text-accent font-medium">{testimonial.service}</div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="hidden md:block">
                <CarouselPrevious className="absolute -left-4 top-1/2 -translate-y-1/2 bg-background shadow-md border-border text-primary hover:bg-primary hover:text-primary-foreground" />
                <CarouselNext className="absolute -right-4 top-1/2 -translate-y-1/2 bg-background shadow-md border-border text-primary hover:bg-primary hover:text-primary-foreground" />
              </div>
            </Carousel>
          </div>
        </section>

        {/* Specials Grid */}
        <section className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* ChiroThin Special */}
            <Link to="/contact" className="block group h-full">
              <Card className="bg-card border-border shadow-lg overflow-hidden flex flex-col h-full transition-shadow hover:shadow-xl">
                <div className="h-48 overflow-hidden bg-white p-4 flex items-center justify-center border-b">
                  <img 
                    src="https://wsrv.nl/?url=https://chirothin.com/logo.png&w=600&output=webp&q=80" 
                    alt="ChiroThin Weight Loss" 
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-[80%] object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 flex-grow flex flex-col">
                  <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-4 w-fit">
                    <Activity className="mr-2 h-4 w-4" />
                    ChiroThin
                  </div>
                  <h2 className="text-2xl font-bold text-primary mb-2 group-hover:text-accent transition-colors">ChiroThin Special</h2>
                  <h3 className="text-xl font-semibold text-accent mb-4">9-Day Trial for $149</h3>
                  <p className="text-muted-foreground mb-6 flex-grow">
                    Kickstart your weight loss journey with our exclusive 9-day ChiroThin trial. Includes a consultation, body composition analysis, the ChiroThin supplement, and customized dietary guidelines.
                  </p>
                  <div className="flex items-center justify-center w-full bg-accent text-white py-2 rounded-md font-medium group-hover:bg-accent/90 transition-colors">
                    Claim Offer & Book <ChevronRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Decompression Special */}
            <Link to="/appointment-scheduling" className="block group h-full">
              <Card className="bg-card border-border shadow-lg overflow-hidden flex flex-col h-full transition-shadow hover:shadow-xl">
                <div className="h-48 overflow-hidden bg-white p-4 flex items-center justify-center border-b">
                  <img 
                    src="https://wsrv.nl/?url=https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/661545aa9b258827b2ecd49a.webp&w=600&output=webp&q=80" 
                    alt="Spinal Decompression" 
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 flex-grow flex flex-col">
                  <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-4 w-fit">
                    <PersonStanding className="mr-2 h-4 w-4" />
                    DRX9000 Decompression
                  </div>
                  <h2 className="text-2xl font-bold text-primary mb-2 group-hover:text-accent transition-colors">Decompression Special</h2>
                  <h3 className="text-xl font-semibold text-accent mb-4">Receive a $79 Consultation</h3>
                  <p className="text-muted-foreground mb-6 flex-grow">
                    Embark on a journey to relief and rejuvenation with our special $79 decompression consultation. Decompression therapy is a non-surgical, painless, and effective treatment method designed to alleviate back and neck pain, herniated discs, and other spinal conditions.
                  </p>
                  <div className="flex items-center justify-center w-full bg-accent text-white py-2 rounded-md font-medium group-hover:bg-accent/90 transition-colors">
                    Claim Offer & Book <ChevronRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Red Light Special */}
            <Link to="/appointment-scheduling" className="block group h-full">
              <Card className="bg-card border-border shadow-lg overflow-hidden flex flex-col h-full transition-shadow hover:shadow-xl">
                <div className="h-48 overflow-hidden bg-black flex items-center justify-center border-b">
                  <img 
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/e3fefda6-4541-41e2-b1b1-3631721c72b4.png&w=600&output=webp&q=80" 
                    alt="Red Light Therapy" 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 flex-grow flex flex-col">
                  <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-4 w-fit">
                    <Sun className="mr-2 h-4 w-4" />
                    Red Light Therapy
                  </div>
                  <h2 className="text-2xl font-bold text-primary mb-2 group-hover:text-accent transition-colors">Red Light Special</h2>
                  <h3 className="text-xl font-semibold text-accent mb-4">Introductory Consultation</h3>
                  <p className="text-muted-foreground mb-6 flex-grow">
                    Experience the incredible benefits of our cutting-edge Dahlia Red Light Body Contouring Technology. Target stubborn areas, boost cellular energy, reduce inflammation, and improve your overall skin health with our non-invasive introductory session.
                  </p>
                  <div className="flex items-center justify-center w-full bg-accent text-white py-2 rounded-md font-medium group-hover:bg-accent/90 transition-colors">
                    Claim Offer & Book <ChevronRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* SoftWave Special */}
            <Link to="/appointment-scheduling" className="block group h-full">
              <Card className="bg-card border-border shadow-lg overflow-hidden flex flex-col h-full transition-shadow hover:shadow-xl">
                <div className="h-48 overflow-hidden bg-white p-4 flex items-center justify-center border-b">
                  <img 
                    src="https://wsrv.nl/?url=https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/c82b52bd-a80b-404c-a2ca-0881d9da6765.png&w=600&output=webp&q=80" 
                    alt="SoftWave Therapy" 
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 flex-grow flex flex-col">
                  <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-4 w-fit">
                    <Activity className="mr-2 h-4 w-4" />
                    SoftWave TRT
                  </div>
                  <h2 className="text-2xl font-bold text-primary mb-2 group-hover:text-accent transition-colors">SoftWave Special</h2>
                  <h3 className="text-xl font-semibold text-accent mb-4">Receive a $79 Consultation</h3>
                  <p className="text-muted-foreground mb-6 flex-grow">
                    Discover the transformative power of SoftWave therapy with our exclusive $79 consultation offer. Softwave technology harnesses the latest advancements in non-invasive treatment to promote healing, reduce pain, and enhance tissue regeneration.
                  </p>
                  <div className="flex items-center justify-center w-full bg-accent text-white py-2 rounded-md font-medium group-hover:bg-accent/90 transition-colors">
                    Claim Offer & Book <ChevronRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>

          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
